import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-na-vypusknoj")!;

const faqItems = [
  { q: "Можно ли заказать песню на выпускной за один день?", a: "Да. Демоверсия создаётся за 3 минуты. Даже если выпускной сегодня вечером — вы успеете." },
  { q: "Как сделать песню про весь класс?", a: "Расскажите самые яркие истории: прозвища учителей, смешные случаи, совместные поездки." },
  { q: "Какой стиль подойдёт для выпускного?", a: "Для торжественного момента — баллада или поп. Для весёлого вечера — рэп, рок или частушки." },
  { q: "Можно ли включить песню на сцене?", a: "Конечно. Полная версия скачивается в высоком качестве." },
  { q: "Сколько стоит песня на выпускной?", a: "Демоверсия бесплатна. Полная версия — от 249 ₽." },
];

const PesnyaNaVypusknoj = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня на выпускной на заказ</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Актовый зал. Шары под потолком. И вдруг — из колонок звучит <strong className="text-foreground">песня про ваш класс</strong>. С именами. С теми самыми историями.</p>
            <p><strong className="text-foreground">Персональная песня на выпускной</strong> — трек, написанный специально для вашего класса. Он будет жить в памяти дольше любого фотоальбома.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — лучший подарок на выпускной</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Фотоальбомы пылятся. А песню можно включить через десять лет на встрече выпускников — и снова оказаться в том зале.</p>
              <p>Это подарок, который объединяет. Его можно отправить в общий чат, добавить в плейлист.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎓 Создать песню на выпускной</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Для какого выпускного подойдёт песня</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Школьный выпускной.</strong> 9 или 11 класс — неважно. Главное — истории.</p>
              <p><strong className="text-foreground">Университетский выпускной.</strong> Четыре-шесть лет общаги и сессий — в одном треке.</p>
              <p><strong className="text-foreground">Подарок учителю.</strong> Песня от всего класса сильнее любого букета. Подробнее — <Link to="/pesnya-v-podarok" className="text-primary hover:underline">песня в подарок</Link>.</p>
              <p><strong className="text-foreground">Подарок от родителей.</strong> Загляните на страницу <Link to="/pesnya-rebenku-v-podarok" className="text-primary hover:underline">песня ребёнку в подарок</Link>.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Классная плакала. А мы — смеялись и плакали одновременно»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Староста 11-Б написала всё: как их класс прозвали «дикие», как они выиграли КВН. На выпускном включили на сцене. Весь зал подпевал припев — хотя слышал песню впервые.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Создать гимн вашего класса</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о песнях на выпускной</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть ваш выпускной зазвучит навсегда</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте песню, которую вы будете слушать на встрече через 10 лет.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaNaVypusknoj;
